import { RefObject, useCallback, useLayoutEffect } from 'react'
import { useIsMobile } from '../../hooks/useMediaQuery'
import { getHeaderLabel } from './utils'
import './mobileCards.css'

function expandHeaderRow(row: HTMLTableRowElement) {
  const cols: { label: string; start: number }[] = []
  Array.from(row.cells).forEach((th) => {
    const label = getHeaderLabel(th as HTMLTableCellElement)
    const start = cols.length
    for (let i = 0; i < th.colSpan; i++) cols.push({ label, start })
  })
  return cols
}

// Elemento attivato toccando la card su mobile: un link marcato con data-card-link,
// altrimenti il primo link (dettaglio) o la checkbox (selezione) della prima cella
function getCardLink(row: HTMLTableRowElement) {
  const explicit = row.querySelector<HTMLElement>('a[data-card-link]')
  if (explicit) return explicit
  const first = row.cells[0]
  if (!first || first.colSpan > 1) return null
  return first.querySelector<HTMLElement>('a[href], input[type="checkbox"]:not(:disabled)')
}

const INTERACTIVE = 'a, button, input, select, textarea, label, .list-group, [role="button"]'

function isEmptyCell(cell: HTMLTableCellElement) {
  return (
    !cell.textContent?.trim() &&
    !cell.querySelector('button, input, select, textarea, img, svg')
  )
}

function setAttr(el: Element, name: string, value: string | null) {
  if (value === null) {
    if (el.hasAttribute(name)) el.removeAttribute(name)
  } else if (el.getAttribute(name) !== value) {
    el.setAttribute(name, value)
  }
}

// Annota le celle con attributi usati dal CSS per mostrare le righe come card su mobile:
// - data-label: testo dell'intestazione della colonna
// - data-group: intestazione di gruppo (riga superiore del thead), sulla prima cella non vuota del gruppo
// - data-empty: cella senza contenuto, nascosta nella card
// - data-card-action: unica cella senza intestazione con un pulsante, mostrata in alto a destra
// - data-group-end: ultima cella visibile di un gruppo, con padding inferiore
// - data-card-link (sulla riga): la card intera apre il link al dettaglio
// - data-last-visible: ultima cella mostrata nella card, con gli angoli inferiori arrotondati
function applyCellLabels(table: HTMLTableElement) {
  const headerRows = table.tHead ? Array.from(table.tHead.rows) : []
  if (headerRows.length === 0) return

  const labels = expandHeaderRow(headerRows[headerRows.length - 1])
  const groups = headerRows.length > 1 ? expandHeaderRow(headerRows[headerRows.length - 2]) : []

  Array.from(table.tBodies).forEach((tbody) => {
    Array.from(tbody.rows).forEach((row) => {
      let col = 0
      const shownGroups = new Set<number>()
      const cellGroups = new Map<HTMLTableCellElement, number>()
      const actionCells: HTMLTableCellElement[] = []

      Array.from(row.cells).forEach((cell, index) => {
        const fullRow = cell.colSpan > 1
        const label = fullRow ? '' : labels[col]?.label ?? ''
        const group = fullRow ? undefined : groups[col]
        const empty = !fullRow && isEmptyCell(cell)

        setAttr(cell, 'data-label', label || null)
        setAttr(cell, 'data-empty', empty ? '' : null)

        if (group?.label) cellGroups.set(cell, group.start)

        if (group?.label && !empty && !shownGroups.has(group.start)) {
          shownGroups.add(group.start)
          setAttr(cell, 'data-group', group.label)
        } else {
          setAttr(cell, 'data-group', null)
        }

        if (!label && !fullRow && index > 0 && cell.querySelector('button, .btn')) {
          actionCells.push(cell)
        }
        col += cell.colSpan
      })

      setAttr(row, 'data-card-link', getCardLink(row) ? '' : null)

      const visibleCells = Array.from(row.cells).filter(
        (cell) => !cell.hasAttribute('data-empty') && !(actionCells.length === 1 && actionCells[0] === cell)
      )
      const lastVisible = visibleCells[visibleCells.length - 1]
      const groupEnds = visibleCells.filter(
        (cell, i) => cellGroups.has(cell) && cellGroups.get(cell) !== cellGroups.get(visibleCells[i + 1])
      )

      Array.from(row.cells).forEach((cell) => {
        const isAction = actionCells.length === 1 && actionCells[0] === cell
        setAttr(cell, 'data-card-action', isAction ? '' : null)
        setAttr(cell, 'data-last-visible', cell === lastVisible ? '' : null)
        setAttr(cell, 'data-group-end', groupEnds.includes(cell) ? '' : null)
      })
    })
  })
}

// Su mobile (con la classe CSS "mobile-cards" su un contenitore) le righe della tabella
// diventano card: annota le celle e restituisce l'handler che rende cliccabile tutta la card
export function useMobileCards(tableRef: RefObject<HTMLTableElement>) {
  const isMobile = useIsMobile()

  useLayoutEffect(() => {
    const table = tableRef.current
    if (!table) return
    applyCellLabels(table)
    const observer = new MutationObserver(() => applyCellLabels(table))
    observer.observe(table, { childList: true, subtree: true, characterData: true })
    return () => observer.disconnect()
  }, [tableRef])

  // Il tocco su tutta la card apre il dettaglio,
  // tranne quando si tocca un elemento interattivo (link, pulsanti, menu)
  return useCallback(
    (e: React.MouseEvent<HTMLTableElement>) => {
      if (!isMobile) return
      const target = e.target as HTMLElement
      if (target.closest(INTERACTIVE)) return
      const row = target.closest('tbody > tr')
      if (!(row instanceof HTMLTableRowElement)) return
      getCardLink(row)?.click()
    },
    [isMobile]
  )
}
